import axios from "../config";
import {
  chatFilesRevoked,
  dropChatFileEntries,
  forgetChatFileOwners,
  ownsChatFile,
  recordChatFile,
  revokeChatFiles,
} from "./chatFileOwners";

export const getUnreadCount = () => {
  return axios.get("/chat/unread-count");
};

// Downloaded chat files are kept in the browser (like WhatsApp Web), so a reload
// doesn't download them again. Cleared on logout.
const FILES_CACHE = "chat-files";
const fileKey = (messageId) => `/chat-files/${messageId}`;

// Kept only as this login's, and only while the chat is still mine (chatFileOwners)
export const storeChatFile = async (messageId, blob, conversationId) => {
  if (!(await recordChatFile(messageId, conversationId))) return;
  try {
    const cache = await window.caches?.open(FILES_CACHE);
    await cache?.put(fileKey(messageId), new Response(blob));
  } catch {
    // Storage full or unavailable - it just downloads again next time
  }
};

// A blob URL for a file downloaded before in this browser, or null
export const getStoredChatFileUrl = async (messageId) => {
  // Another login's (or one nobody can be named for) is never handed back
  if (!(await ownsChatFile(messageId))) return null;
  try {
    const cache = await window.caches?.open(FILES_CACHE);
    const stored = await cache?.match(fileKey(messageId));
    return stored ? URL.createObjectURL(await stored.blob()) : null;
  } catch {
    return null;
  }
};

// Profile photos, by attachment id. A new photo is a new attachment, so a stored one
// never goes stale. Kept like chat files, so avatars show straight away after a reload.
const PHOTOS_CACHE = "chat-photos";
const photoKey = (attachmentId) => `/chat-photos/${attachmentId}`;

// A blob URL for the photo - from this browser if it's been seen before, else downloaded
// (and kept). Throws if it can't be downloaded.
export const getProfilePhotoUrl = async (attachmentId) => {
  try {
    const cache = await window.caches?.open(PHOTOS_CACHE);
    const stored = await cache?.match(photoKey(attachmentId));
    if (stored) return URL.createObjectURL(await stored.blob());
  } catch {
    // Storage unavailable - download it
  }
  const response = await axios.get(`attachment/${attachmentId}`, { responseType: "blob" });
  try {
    const cache = await window.caches?.open(PHOTOS_CACHE);
    await cache?.put(photoKey(attachmentId), new Response(response.data));
  } catch {
    // Storage full or unavailable - it just downloads again next time
  }
  return URL.createObjectURL(response.data);
};

// On logout: chat files and profile photos both go
export const clearChatFiles = async () => {
  await forgetChatFileOwners();
  try {
    await window.caches?.delete(FILES_CACHE);
    await window.caches?.delete(PHOTOS_CACHE);
  } catch {
    // Nothing stored
  }
};

// Taken out of a chat: its files kept in this browser go too (the server refuses them from now
// on, but a kept copy would still open)
export const removeStoredChatFiles = async (messageIds) => {
  try {
    const cache = await window.caches?.open(FILES_CACHE);
    await Promise.all(messageIds.map((messageId) => cache?.delete(fileKey(messageId))));
  } catch {
    // Nothing stored
  }
  await dropChatFileEntries(messageIds);
};

// Taken out of a chat: every file of it kept for this login goes, whichever message it was
// from (not only those still on screen). Resolves their message ids
export const removeConversationFiles = (conversationId) => revokeChatFiles(conversationId);

// Chat files are fetched with the auth header, so they come back as a blob URL.
// onProgress gets axios progress events (with a total only if the server sends one).
export const getChatAttachmentUrl = async (messageId, onProgress, conversationId) => {
  const response = await axios.get(`chat/messages/${messageId}/attachment`, {
    responseType: "blob",
    ...(onProgress && { onDownloadProgress: onProgress }),
  });
  // Taken out of the chat while it downloaded: not kept, not shown
  if (chatFilesRevoked(conversationId)) throw new Error("No longer in this chat");
  storeChatFile(messageId, response.data, conversationId);
  return URL.createObjectURL(response.data);
};

/* ----- Groups, contacts and search (CD-1335) ----- */

// Who I can start a direct message with ("direct") or add to a group ("group").
// Never every staff member - the server applies the company's DM scope.
// -> { staff: [{ id, name, avatar, teams, online, lastSeenAt, existingConversationId? }] }
export const getChatContacts = (params, config) => axios.get("/chat/contacts", { params, ...config });

// My Care Diary teams for "Team chat" (every live team for an overseer)
// -> { canStart, teams: [{ id, name, memberCount, conversationId }] }
export const getChatTeams = () => axios.get("/chat/teams");
// Opens a team's chat, starting it the first time -> ConversationDetail
export const startChatTeamConversation = (teamId) => axios.post(`/chat/teams/${teamId}`);

// -> ConversationDetail
export const createChatGroup = (data) => axios.post("/chat/groups", data);
export const updateChatGroup = (conversationId, data) => axios.put(`/chat/groups/${conversationId}`, data);
export const addChatGroupMembers = (conversationId, staffIds) =>
  axios.post(`/chat/groups/${conversationId}/members`, { staffIds });
export const removeChatGroupMember = (conversationId, staffId) =>
  axios.delete(`/chat/groups/${conversationId}/members/${staffId}`);
export const setChatGroupMemberRole = (conversationId, staffId, role) =>
  axios.put(`/chat/groups/${conversationId}/members/${staffId}/role`, { role });
export const leaveChatGroup = (conversationId) => axios.post(`/chat/groups/${conversationId}/leave`);
export const archiveChatGroup = (conversationId) => axios.delete(`/chat/groups/${conversationId}`);
export const unarchiveChatGroup = (conversationId) => axios.post(`/chat/groups/${conversationId}/unarchive`);

// Across every chat I can read when conversationId is left out -> { data, hasMore }
export const searchChatMessages = (params, config) => axios.get("/chat/search", { params, ...config });

/* ----- Round 3: starred chats, delete for me, files ----- */

// Mine only - starred chats sort first in my list -> { starred }
export const setChatConversationStar = (conversationId, starred) =>
  axios.put(`/chat/conversations/${conversationId}/star`, { starred });

// "Clear chat" / "Delete chat" (a DM): everything so far goes from my view only -> { clearedSeq }
export const clearChatConversation = (conversationId) => axios.post(`/chat/conversations/${conversationId}/clear`);

// Hides the message for me only (anyone's, not system messages) -> 204
export const deleteChatMessageForMe = (messageId) =>
  axios.delete(`/chat/messages/${messageId}`, { params: { scope: "me" } });

// Message info: who a message of mine went to, who has read it and, where recorded, when
export const getChatMessageInfo = (messageId) => axios.get(`/chat/messages/${messageId}/info`);

// The Shared view: a page of one category (media, documents or links), newest first ->
// { data, hasMore }. beforeSeq (a month's newest seq + 1) and offset reach any month directly
export const getChatSharedItems = (conversationId, params) =>
  axios.get(`/chat/conversations/${conversationId}/shared`, { params });

// The months a Shared category has anything in, newest first, with exact counts -> { data }
export const getChatSharedMonths = (conversationId, params) =>
  axios.get(`/chat/conversations/${conversationId}/shared/months`, { params });

// Everything with a file in a conversation, newest first -> { data, hasMore }
export const getChatConversationAttachments = (conversationId, params) =>
  axios.get(`/chat/conversations/${conversationId}/attachments`, { params });
